import { FrontSide, MeshPhysicalMaterial, ShaderLib, UniformsUtils, Vector2 } from 'three';

import { modifySource } from '@resn/gozer-three';

import { shaderChunks as ShaderChunksPreReveal } from '../shaders/ShaderChunks/pre-reveal';
import { shaderChunks as ShaderChunksWipe } from '../shaders/ShaderChunks/wipe-reveal';
import glslBlend from '@/libs/lygia/color/blend.glsl';
import glslSaturate from '@/libs/lygia/math/saturate.glsl';
import glslSum from '@/libs/lygia/math/sum.glsl';

class GoldenTicketMaterial extends MeshPhysicalMaterial {
    constructor(
        props = {},
        options = {
            flipUV: false,
        }
    ) {
        // Default properties for golden ticket material
        props = Object.assign(
            {
                name: 'GoldenTicketMaterial',
                transparent: true,
                clearcoat: 0.1,
                clearcoatRoughness: 0.01,
                ior: 1.0,
                roughness: 0.2,
                metalness: 0.8,
                side: FrontSide,
                normalScale: new Vector2(0.5, -0.5),
                fog: false,
            },
            props
        );

        super(props);
        this.defines = {
            PHYSICAL: '',
            STANDARD: '',

            USE_UV: true,
            USE_RGB: true,
            USE_WIPE: true,
            USE_PRE_REVEAL: true,
            USE_INNERMAP: true,
            USE_FLIP_UV: options.flipUV,

            // USE_DEBUG: true,

            ...props.defines,
        };

        this.onBeforeCompile = (shader) => {
            shader.fragmentShader = modifySource(
                fragmentShader,
                {
                    uniforms: 'insertbefore:#include <common>',
                    functions: 'insertafter:#include <clipping_planes_pars_fragment>',
                    preLights: 'insertbefore:#include <lights_physical_fragment>',
                    postFragColor: 'insertafter:#include <opaque_fragment>',
                    replaceFog: 'replace:#include <fog_fragment>',
                },
                {
                    uniforms: /* glsl */ `
                        uniform sampler2D rgbMap;
                        uniform sampler2D innerMap;
                        uniform sampler2D uNoise;
                        
                        uniform float uTime;
                    `,
                    functions: /* glsl */ `
                        ${glslBlend}
                        ${glslSum}
                        ${glslSaturate}

                        // Add UV flipping utility function
                        vec2 flipUV(vec2 uv) {
                            #ifdef USE_FLIP_UV
                                return vec2(uv.x, 1.0 - uv.y);
                            #else
                                return uv;
                            #endif
                        }
                        
                        #ifdef USE_WIPE
                            ${ShaderChunksWipe.funcs}
                        #endif
                        
                        #ifdef USE_PRE_REVEAL
                            ${ShaderChunksPreReveal.funcs}
                        #endif
                    `,
                    preLights: shaderChunks.preLights,
                    postFragColor: shaderChunks.postFragColor,
                    replaceFog: shaderChunks.replaceFog,
                }
            );

            Object.assign(shader.uniforms, this.uniforms);
        };

        const uniforms = {
            ...ShaderChunksWipe.uniforms(),
            ...ShaderChunksPreReveal.uniforms(),
            ...{
                rgbMap: { value: null },
                innerMap: { value: null },
                uNoise: { value: null },
                uTime: { value: 0 },
            },
        };
        this.uniforms = UniformsUtils.merge([ShaderLib.physical.uniforms, uniforms, this.uniforms]);
    }

    set rgbMap(value) {
        if (this.uniforms.rgbMap) {
            this.uniforms.rgbMap.value = value;
            this.needsUpdate = true;
        }
    }

    get rgbMap() {
        return this.uniforms.rgbMap?.value || null;
    }

    set innerMap(value) {
        if (this.uniforms.innerMap) {
            this.uniforms.innerMap.value = value;
            this.needsUpdate = true;
        }
    }

    get innerMap() {
        return this.uniforms.innerMap?.value || null;
    }
}

export default GoldenTicketMaterial;

const fragmentShader = ShaderLib.physical.fragmentShader;

const shaderChunks = {
    preLights: /* glsl */ `
        // RGB channel to define materials
        vec3 cRGB = vec3(0.0);
        #ifdef USE_RGB
            cRGB = texture2D(rgbMap, vUv).rgb;
        
            metalnessFactor *= saturate(0.1 + cRGB.r * 0.5);
            roughnessFactor *= saturate(0.5 + cRGB.r);

            #ifdef USE_INNERMAP
                vec4 diffuseInner = texture2D(innerMap, vUv);
                diffuseColor.rgb = mix(diffuseColor.rgb, diffuseInner.rgb, cRGB.r);
            #endif
        #endif
        
        #ifdef USE_PRE_REVEAL
            vec3 outPreReveal = preReveal(vUv, 0.86, uTexPreRevealSticker, 2.2, uTime, false);
            vec4 outPreRevealSticker = getColorSticker(flipUV(vUv), vec2(1.0, 1.76), 2.25, uTexPreRevealSticker);
        #endif

        #ifdef USE_WIPE
            vec4 outWipe = wipe(vUv,  uWipeAngle,  uWipeRevealPr,  uPreRevealColor2,  uPreRevealColor1,  uPreRevealColor2,  uNoise,  uTime);
        #endif
    `,
    postFragColor: /* glsl */ `
        #ifdef USE_RGB
            outgoingLight = blendDarken(outgoingLight, vec3(0.), cRGB.r * 0.5);
        #endif

        #ifdef USE_PRE_REVEAL
            #ifdef USE_WIPE
                // sticker color effect
                outPreReveal = mix(
                    outPreReveal, 
                    uPreRevealColor1, 
                    outPreRevealSticker.a * length(radiance) * 0.08
                ); 
                outgoingLight.rgb = mix(outPreReveal, outgoingLight, saturate(outWipe.a * 2.));
            #else
                outgoingLight.rgb = outPreReveal;
            #endif
        #endif

        #ifdef USE_WIPE
            outgoingLight.rgb = blendScreen(outgoingLight.rgb, outWipe.rgb);
        #endif
            
        #ifdef USE_DEBUG
            // outgoingLight.rgb = diffuseColor.rgb;
            #ifdef USE_WIPE
                // gl_FragColor.rgb = outWipe.rgb;
                // gl_FragColor.rgb = vec3(outWipe.a);
            #endif
            #ifdef USE_PRE_REVEAL
                // gl_FragColor.rgb = outPreReveal;
                // gl_FragColor.rgb = radiance;
            #endif
            // gl_FragColor = vec4(vec3(outWipe.a), diffuseColor.a);
            gl_FragColor = diffuseColor;
        #else
            gl_FragColor = vec4(outgoingLight, diffuseColor.a);
        #endif

    `,
    replaceFog: /* glsl */ `
        #ifdef USE_FOG
            float fogFactor = smoothstep(fogNear, fogFar, vWorldPosition.z);
            gl_FragColor.rgb *= fogFactor;
        #endif
    `,
};
